import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const CHART_HEIGHT = 320;
const MARGIN = { top: 20, right: 20, bottom: 50, left: 60 };

const SPREAD_BADGES = {
  widening: { bg: '#fee2e2', color: '#991b1b', label: 'WIDENING', arrow: '\u2191' },
  narrowing: { bg: '#dcfce7', color: '#166534', label: 'NARROWING', arrow: '\u2193' },
  stable: { bg: '#f3f4f6', color: '#6b7280', label: 'STABLE', arrow: '\u2192' },
};

const QUANTILE_COLORS = {
  0.10: { line: '#bfdbfe', fill: '#eff6ff' },
  0.25: { line: '#93c5fd', fill: '#dbeafe' },
  0.50: { line: '#2563eb', fill: null },
  0.75: { line: '#93c5fd', fill: '#dbeafe' },
  0.90: { line: '#bfdbfe', fill: '#eff6ff' },
};

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '4px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 8px',
    borderRadius: '9999px',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.025em',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '12px',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  container: {
    overflowX: 'auto',
  },
  spreadInfo: {
    display: 'flex',
    gap: '24px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  spreadStat: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  spreadValue: {
    fontWeight: 600,
    color: '#1f2937',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

export default function QuantileRegressionFan({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.dates || data.dates.length === 0 || !data.quantiles || data.quantiles.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 600;
    const width = Math.max(600, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', CHART_HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    const dates = data.dates;
    const n = dates.length;

    // Index quantiles by q value for easy lookup
    const quantileMap = {};
    data.quantiles.forEach((qObj) => {
      quantileMap[qObj.q] = qObj;
    });

    // Collect all values for Y domain
    const allValues = data.quantiles.flatMap((qObj) => qObj.values).filter((v) => v != null);
    const priceMin = d3.min(allValues);
    const priceMax = d3.max(allValues);
    const pricePadding = (priceMax - priceMin) * 0.1 || 1;

    // Scales
    const x = d3.scalePoint()
      .domain(dates)
      .range([0, innerW]);

    const y = d3.scaleLinear()
      .domain([priceMin - pricePadding, priceMax + pricePadding])
      .nice()
      .range([innerH, 0]);

    // Horizontal grid lines
    const yTicks = y.ticks(6);
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(d3.axisBottom(x).tickSize(0).tickFormat((d) => formatDateLabel(d)))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.25em');

    g.select('.domain').attr('stroke', '#e5e7eb');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .ticks(6)
          .tickFormat((v) => `$${v}`)
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.selectAll('.domain').attr('stroke', '#e5e7eb');
    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Helper to build area data from two quantile arrays
    const buildAreaData = (lowerArr, upperArr) =>
      dates.map((date, i) => ({
        date,
        lower: lowerArr[i],
        upper: upperArr[i],
      }));

    const areaGen = d3.area()
      .x((d) => x(d.date))
      .y0((d) => y(d.lower))
      .y1((d) => y(d.upper));

    // Band 1: P10-P90 (outermost, lightest)
    if (quantileMap[0.10] && quantileMap[0.90]) {
      g.append('path')
        .datum(buildAreaData(quantileMap[0.10].values, quantileMap[0.90].values))
        .attr('d', areaGen)
        .attr('fill', '#dbeafe')
        .attr('fill-opacity', 0.4)
        .attr('stroke', 'none');
    }

    // Band 2: P25-P75 (inner, medium)
    if (quantileMap[0.25] && quantileMap[0.75]) {
      g.append('path')
        .datum(buildAreaData(quantileMap[0.25].values, quantileMap[0.75].values))
        .attr('d', areaGen)
        .attr('fill', '#93c5fd')
        .attr('fill-opacity', 0.45)
        .attr('stroke', 'none');
    }

    // Draw each quantile line (thin) within the bands
    // Sort by q so outer quantiles are drawn first, median last (on top)
    const sortedQuantiles = [...data.quantiles].sort((a, b) => {
      // Median (0.50) should be drawn last
      if (a.q === 0.50) return 1;
      if (b.q === 0.50) return -1;
      return Math.abs(a.q - 0.50) - Math.abs(b.q - 0.50);
    });

    sortedQuantiles.forEach((qObj) => {
      const lineGen = d3.line()
        .x((d, i) => x(dates[i]))
        .y((d) => y(d))
        .defined((d) => d != null);

      const isMedian = qObj.q === 0.50;
      const colorEntry = QUANTILE_COLORS[qObj.q] || { line: '#93c5fd' };

      g.append('path')
        .datum(qObj.values)
        .attr('d', lineGen)
        .attr('fill', 'none')
        .attr('stroke', colorEntry.line)
        .attr('stroke-width', isMedian ? 2.5 : 1.2)
        .attr('stroke-dasharray', isMedian ? null : '4,3');
    });

    // Dots on median line
    if (quantileMap[0.50]) {
      quantileMap[0.50].values.forEach((v, i) => {
        if (v == null) return;
        g.append('circle')
          .attr('cx', x(dates[i]))
          .attr('cy', y(v))
          .attr('r', 3)
          .attr('fill', '#2563eb');
      });
    }

    // Crosshair line (initially hidden)
    const crosshair = g.append('line')
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('display', 'none');

    // Invisible overlay columns for tooltip hover
    const colWidth = n > 1 ? innerW / (n - 1) : innerW;

    dates.forEach((date, i) => {
      const cx = x(date);
      const halfW = colWidth / 2;

      g.append('rect')
        .attr('x', cx - halfW)
        .attr('y', 0)
        .attr('width', colWidth)
        .attr('height', innerH)
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          crosshair
            .attr('x1', cx)
            .attr('x2', cx)
            .style('display', null);

          const quantileValues = {};
          data.quantiles.forEach((qObj) => {
            quantileValues[qObj.q] = {
              value: qObj.values[i],
              label: qObj.label,
            };
          });

          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            date,
            quantileValues,
          });
        })
        .on('mouseleave', () => {
          crosshair.style('display', 'none');
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Empty state
  if (!data || !data.dates || data.dates.length === 0 || !data.quantiles || data.quantiles.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.headerRow}>
          <div style={styles.title}>Quantile Regression Fan</div>
        </div>
        <div style={styles.subtitle}>Conditional price quantiles over time</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No quantile data available
        </div>
      </div>
    );
  }

  const spread = data.spread || {};
  const badge = SPREAD_BADGES[spread.trend] || SPREAD_BADGES.stable;

  // Build tooltip rows sorted from highest quantile to lowest
  const tooltipRows = tooltip
    ? [...data.quantiles]
        .sort((a, b) => b.q - a.q)
        .map((qObj) => {
          const isMedian = qObj.q === 0.50;
          const colorEntry = QUANTILE_COLORS[qObj.q] || { line: '#93c5fd' };
          return {
            label: qObj.label,
            value: tooltip.quantileValues[qObj.q]?.value,
            color: colorEntry.line,
            bold: isMedian,
          };
        })
    : [];

  return (
    <div style={styles.card}>
      <div style={styles.headerRow}>
        <div style={styles.title}>Quantile Regression Fan</div>
        {spread.trend && (
          <span
            style={{
              ...styles.badge,
              background: badge.bg,
              color: badge.color,
            }}
          >
            {badge.label} {badge.arrow}
          </span>
        )}
      </div>
      <div style={styles.subtitle}>
        Conditional price quantiles over time
        {data.field ? ` (${data.field})` : ''}
      </div>

      {/* Spread indicator */}
      {spread.initial != null && spread.current != null && (
        <div style={styles.spreadInfo}>
          <div style={styles.spreadStat}>
            <span>Initial spread (P90-P10):</span>
            <span style={styles.spreadValue}>${spread.initial.toFixed(2)}</span>
          </div>
          <div style={styles.spreadStat}>
            <span>Current spread:</span>
            <span style={styles.spreadValue}>${spread.current.toFixed(2)}</span>
          </div>
          <div style={styles.spreadStat}>
            <span>Change:</span>
            <span style={{
              ...styles.spreadValue,
              color: spread.current > spread.initial ? '#dc2626'
                : spread.current < spread.initial ? '#16a34a'
                : '#6b7280',
            }}>
              {spread.current > spread.initial ? '+' : ''}
              ${(spread.current - spread.initial).toFixed(2)}
            </span>
          </div>
        </div>
      )}

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#2563eb" strokeWidth="2.5" />
          </svg>
          <span>Median (50th)</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <rect x="0" y="0" width="20" height="10" fill="#93c5fd" fillOpacity="0.45" rx="2" />
          </svg>
          <span>P25-P75</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <rect x="0" y="0" width="20" height="10" fill="#dbeafe" fillOpacity="0.4" rx="2" />
          </svg>
          <span>P10-P90</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#bfdbfe" strokeWidth="1.2" strokeDasharray="4,3" />
          </svg>
          <span>Quantile Lines</span>
        </div>
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {formatDateFull(tooltip.date)}
          </div>
          {tooltipRows.map((row) => (
            <div
              key={row.label}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '16px',
                marginBottom: '2px',
              }}
            >
              <span style={{ color: row.color, fontWeight: row.bold ? 600 : 400 }}>
                {row.label}
              </span>
              <span style={{ fontWeight: 600 }}>
                ${row.value != null ? row.value.toFixed(2) : '--'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
